import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Placeholder } from "react-bootstrap";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  ArrowRight,
  BarChart3,
  Clock,
  Folder,
  Hourglass,
  ShieldAlert,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import AppShell from "../../components/AppShell";
import NotificationIcon from "../../components/Notification";
import PageHeader from "../../components/PageHeader";
import { useUser } from "../../contextProviders/userContext";
import "../student/StudentDashboard.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const BLUE = "#1B3A87";
const LINE = "#D8DFEC";
const INK = "#17213A";
const FONT = "Source Sans 3";
const numberFormat = new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 1 });

const Metric = ({ label, value, detail, icon: Icon }) => (
  <div className="student-metric">
    <div className="student-metric__copy">
      <span>{label}</span>
      <strong className="wcco-technical">{value}</strong>
      <small>{detail}</small>
    </div>
    <Icon size={20} aria-hidden="true" />
  </div>
);

const SectionHeader = ({ title, description, action }) => (
  <div className="student-section-header">
    <div>
      <h2>{title}</h2>
      {description && <p>{description}</p>}
    </div>
    {action}
  </div>
);

const OrganizationDashboard = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get("/reports/organization/summary");
      setReport(response.data);
    } catch (requestError) {
      console.error("Error fetching organisation dashboard:", requestError);
      setError("Your dashboard data could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const projects = useMemo(
    () => [...(report?.projects || [])].sort((a, b) => b.approvedHours - a.approvedHours || a.name.localeCompare(b.name)),
    [report]
  );

  const chartData = {
    labels: projects.map((project) => project.name),
    datasets: [
      { label: "Approved", data: projects.map((project) => project.approvedHours), backgroundColor: BLUE, borderRadius: 2, barThickness: 13 },
      { label: "Pending", data: projects.map((project) => project.pendingHours), backgroundColor: "rgba(27, 58, 135, 0.30)", borderRadius: 2, barThickness: 13 },
    ],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    animation: { duration: 250 },
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, color: INK, font: { family: FONT, size: 12 } } },
      tooltip: { displayColors: false, titleFont: { family: FONT }, bodyFont: { family: FONT } },
    },
    scales: {
      x: { stacked: true, beginAtZero: true, grid: { color: LINE }, border: { display: false }, ticks: { color: INK, precision: 0, font: { family: FONT, size: 11 } } },
      y: { stacked: true, grid: { display: false }, border: { color: LINE }, ticks: { color: INK, font: { family: FONT, size: 11 }, callback(value) { const label = this.getLabelForValue(value); return label.length > 24 ? `${label.slice(0, 23)}…` : label; } } },
    },
  };

  if (loading) {
    return (
      <AppShell>
        <main className="student-dashboard">
          <PageHeader
            title={<Placeholder animation="glow"><Placeholder xs={4} /></Placeholder>}
            description={<Placeholder animation="glow"><Placeholder xs={8} /></Placeholder>}
          />
          <section className="student-metrics" aria-hidden="true">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="student-metric">
                <div className="student-metric__copy w-100">
                  <span><Placeholder animation="glow"><Placeholder xs={6} /></Placeholder></span>
                  <strong className="wcco-technical"><Placeholder animation="glow"><Placeholder xs={4} size="lg" /></Placeholder></strong>
                </div>
              </div>
            ))}
          </section>
        </main>
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell>
        <Alert className="d-flex align-items-center gap-2"><ShieldAlert size={18} />{error}<Button className="ms-auto" onClick={fetchReport}>Try again</Button></Alert>
      </AppShell>
    );
  }

  const metrics = report?.metrics || {};

  return (
    <AppShell>
      <main className="student-dashboard">
        <PageHeader
          title={`Welcome, ${user?.organizationName || "Organisation"}`}
          description="Track the projects you've posted, the volunteers they've engaged, and their logged hours."
          actions={<><NotificationIcon /><Button onClick={() => navigate("/organization/reports")}>Full report <ArrowRight size={15} /></Button></>}
        />

        <section className="student-metrics" aria-label="Your organisation's activity summary">
          <Metric label="Projects posted" value={metrics.totalProjects ?? 0} detail={`${metrics.pendingProjects ?? 0} awaiting admin review`} icon={Folder} />
          <Metric label="Volunteers engaged" value={metrics.totalStudents ?? 0} detail="Across all your projects" icon={Users} />
          <Metric label="Approved hours" value={numberFormat.format(metrics.approvedHours || 0)} detail="Verified volunteer contribution" icon={Clock} />
          <Metric label="Pending hours" value={numberFormat.format(metrics.pendingHours || 0)} detail="Awaiting leader/admin review" icon={Hourglass} />
        </section>

        <section className="student-dashboard-grid student-dashboard-grid--overview">
          <article className="student-dashboard-panel student-dashboard-panel--chart">
            <SectionHeader title="Hours by project" description="Approved and pending volunteer hours across your projects." action={<BarChart3 size={20} aria-hidden="true" />} />
            {projects.length ? (
              <div className="student-hours-chart" style={{ height: `${Math.min(420, Math.max(240, projects.length * 42 + 80))}px` }}>
                <Bar data={chartData} options={chartOptions} />
              </div>
            ) : <div className="student-panel-state">Post a project to start tracking volunteer contribution.</div>}
          </article>

          <article className="student-dashboard-panel">
            <SectionHeader title="Programme status" description="How your posted projects are progressing." />
            <dl className="student-hour-breakdown">
              <div><dt>Approved projects</dt><dd className="wcco-technical">{metrics.approvedProjects ?? 0}</dd></div>
              <div><dt>Pending approval</dt><dd className="wcco-technical">{metrics.pendingProjects ?? 0}</dd></div>
              <div><dt>Active volunteers</dt><dd className="wcco-technical">{metrics.activeVolunteers ?? 0}</dd></div>
            </dl>
            <div className="student-focus-areas">
              <h3>Project categories</h3>
              {report?.distributions?.categories?.length ? report.distributions.categories.slice(0, 5).map((category) => (
                <div key={category.name}><span>{category.name}</span><strong>{category.projects} project{category.projects === 1 ? "" : "s"}</strong></div>
              )) : <p>No project categories yet.</p>}
            </div>
          </article>
        </section>

        <section className="student-dashboard-panel student-projects-panel">
          <SectionHeader
            title="Our posted projects"
            description="Your programmes and how many volunteers they've engaged."
            action={<Button variant="outline-primary" onClick={() => navigate("/myprojects")}>View projects</Button>}
          />
          {projects.length ? (
            <div className="student-project-list">
              <div className="student-project-list__labels" aria-hidden="true"><span>Project</span><span>Focus</span><span>Volunteers</span><span>Status</span></div>
              {projects.map((project) => (
                <div className="student-project-row" key={`${project.name}-${project.organiser}`}>
                  <strong>{project.name || "Unnamed project"}</strong>
                  <span>{project.categories || "Uncategorised"}</span>
                  <span className="wcco-technical">{project.volunteers}</span>
                  <span className="wcco-status">{project.approved ? "Active" : "Pending approval"}</span>
                </div>
              ))}
            </div>
          ) : <div className="student-panel-state"><span>You have not posted any projects yet.</span><Button onClick={() => navigate("/browse")}>Browse projects</Button></div>}
        </section>
      </main>
    </AppShell>
  );
};

export default OrganizationDashboard;
